import React, { useEffect, useState } from "react";
import { CalendarClock, Monitor, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchLoginDeviceInfo, getDeviceLabel, REMEMBER_DAY_OPTIONS } from "@/utils/deviceInfo";

const RememberMeModal = ({ apiBase, isDark, open, onCancel, onConfirm }) => {
  const [pendingDays, setPendingDays] = useState(1);
  const [deviceInfo, setDeviceInfo] = useState({
    ipAddress: "…",
    deviceLabel: getDeviceLabel(),
  });

  useEffect(() => {
    if (!open || !apiBase) return;
    setPendingDays(1);
    fetchLoginDeviceInfo(apiBase).then(setDeviceInfo);
  }, [open, apiBase]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, onCancel]);

  if (!open) return null;

  const theme = isDark ? "dark" : "light";
  const overlayClass = `login-otp-overlay fixed inset-0 z-[1200] flex items-center justify-center p-4 backdrop-blur-md ${isDark ? "bg-slate-950/70" : "bg-slate-900/35"}`;
  const cardClass = `login-otp-card login-otp-card--${theme} w-full max-w-lg`;
  const headerClass = `login-otp-header login-otp-header--${theme}`;
  const glowClass = `login-otp-header-glow login-otp-header-glow--${theme}`;
  const iconWrapClass = `login-otp-icon-wrap login-otp-icon-wrap--${theme}`;
  const closeClass = `login-otp-close login-otp-close--${theme}`;
  const devicePillClass = `login-otp-phone-pill login-otp-phone-pill--${theme}`;
  const mutedClass = isDark ? "text-slate-400" : "text-slate-500";
  const titleClass = isDark ? "text-white" : "text-slate-900";
  const dayBtnClass = (days) =>
    `login-otp-remember-day login-otp-remember-day--${theme}${pendingDays === days ? " login-otp-remember-day--active" : ""}`;

  const handleConfirm = () => {
    if (pendingDays > 0) {
      onConfirm(pendingDays, deviceInfo.deviceLabel);
    }
  };

  return (
    <div className={overlayClass} role="dialog" aria-modal="true" aria-labelledby="login-remember-title">
      <div className={cardClass}>
        <div className={headerClass}>
          <div className={glowClass} aria-hidden="true" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className={iconWrapClass}>
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h2 id="login-remember-title" className={`text-lg sm:text-xl font-semibold tracking-tight ${titleClass}`}>
                  Bu cihazı hatırla
                </h2>
                <p className={`text-sm mt-1 leading-relaxed ${mutedClass}`}>
                  Seçtiğiniz süre boyunca bu cihazdan OTP olmadan otomatik giriş yapılacaktır.
                </p>
                <div className={devicePillClass}>
                  <Monitor className="w-3.5 h-3.5 shrink-0 opacity-80" />
                  <span className="font-mono tracking-wide">{deviceInfo.ipAddress}</span>
                  <span className="opacity-60">·</span>
                  <span className="truncate">{deviceInfo.deviceLabel}</span>
                </div>
              </div>
            </div>
            <button type="button" onClick={onCancel} className={closeClass} aria-label="Kapat">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="login-otp-body">
          <div className={`login-otp-remember-note login-otp-remember-note--${theme}`}>
            <CalendarClock className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
            <p>
              Yalnızca bu tarayıcı ve IP kombinasyonunda geçerlidir. Başka bir cihazdan girişte tekrar doğrulama istenir.
            </p>
          </div>

          <p className={`text-xs font-medium mb-2.5 ${mutedClass}`}>Otomatik giriş süresi</p>
          <div className="login-otp-remember-grid">
            {REMEMBER_DAY_OPTIONS.map(({ days, label }) => (
              <button
                key={days}
                type="button"
                onClick={() => setPendingDays(days)}
                className={dayBtnClass(days)}
              >
                <span className="login-otp-remember-day-value">{days}</span>
                <span className="login-otp-remember-day-label">gün</span>
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-2.5">
            <Button
              type="button"
              disabled={!pendingDays}
              onClick={handleConfirm}
              className="login-otp-primary-btn"
            >
              Devam
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              className={`h-10 rounded-xl ${mutedClass} ${isDark ? "hover:bg-slate-800/80 hover:text-slate-200" : "hover:bg-slate-100 hover:text-slate-800"}`}
            >
              Vazgeç
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RememberMeModal;
